import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  GlobalStyles,
  Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import styled from "styled-components";
import { APIProvider } from "@vis.gl/react-google-maps";

import Label from "../../../components/form/Label.tsx";
import Map from "./components/Map.jsx";

// Styled components
const MapCanvas = styled.div`
  gap: clamp(8px, 2vw, 20px);
  height: 400px;
  position: relative;

  & > div.metadata-geolocation-map {
    height: 100%;
    width: 100%;
  }

  & > div.metadata-geolocation-map > div {
    border-radius: 8px;
    position: static;
  }
`;

// Step titles
const steps = [
  'Name and Description',
  'Continuous Feed Info',
  'Originator / Owner',
  'Source & Sensor',
  'Geolocation',
  'Environment',
  'Legal & Licensing',
  'Schema',
  'Summary',
];

// Layout component
const GrayBox = ({ children, sx = {} }) => (
  <Box
    sx={{
      backgroundColor: "#f5f5f5",
      p: 2,
      borderRadius: 2,
      mb: 2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Stepper
const CustomStepper = ({ step }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      mb: 4,
      px: 2,
    }}
  >
    {steps.map((_, index) => (
      <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: step === index + 1 ? "#1976d2" : "#726F6F",
            border: "2px solid white",
            zIndex: 2,
          }}
        />

        {index < steps.length - 1 && (
          <Box
            sx={{
              height: "4px",
              backgroundColor: "#726F6F",
              width: 40,
              marginLeft: "-4px",
              marginRight: "-4px",
            }}
          />
        )}
      </Box>
    ))}
  </Box>
);

// Default form data
const defaultFormData = {
  name: "",
  description: "",
  type: "",
  updateInterval: "",
  loadFrequency: "",
  updateIntervalUnit: "Seconds",
  loadFrequencyUnit: "Seconds",
  sector: "",
  sectorEntity: "",
  selectedOriginators: "",
  selectedOwners: "",
  sourceName: "",
  sourceType: "",
  sensorType: "",
  manufacturer: "",
  make: "",
  model: "",
  version: "",
  environment: "",
  interactor: "",
  containerName: "",
  legalType: "",
  regulatoryType: "",
  citation: "",
  fileType: "",
  unitOfMeasure: "",
  schemaBody: "",
  locationType: "Fixed",
  locationSubtype: "Terrestrial",
  locationCategory: "Address",
  streetAddress: "",
  city: "",
  postalCode: "",
  stateProvince: "",
  country: "",
  geoNotes: "",
  latitude: "",
  longitude: "",
  radius: "",
  radiusUnit: "",
  selectedCoordinates: "",
};

interface ShapeData {
  type: "rectangle" | "circle" | "polygon" | null;
  data: any;
}

function serializeShapeData(shapeData) {
  if (!shapeData || !shapeData.type) return "";

  switch (shapeData.type) {
    case "polygon":
      return shapeData.data?.path
        ?.map((latLng, i) => `${i + 1}: Lat: ${latLng.lat()}, Lng: ${latLng.lng()}`)
        .join("\n");
    case "rectangle":
      const bounds = shapeData.data?.bounds;
      return bounds
        ? `Bounds:\nNorth: ${bounds.north}\nEast: ${bounds.east}\nSouth: ${bounds.south}\nWest: ${bounds.west}`
        : "";
    case "circle":
      const circle = shapeData.data;
      return circle
        ? `Center:\nLat: ${circle.center.lat}, Lng: ${circle.center.lng}\nRadius (meters): ${circle.radius}`
        : "";
    default:
      return "";
  }
}

interface CreateMetadataProps {
  onSubmit: (newMetadata: any, isEdit?: boolean) => void;
  initialData?: any;
  isEdit?: boolean;
  startStep?: number;
}


function CreateMetadata({
  onSubmit,
  initialData = null,
  isEdit = false,
  startStep = 1,
}) {
  const [step, setStep] = useState(startStep);

  useEffect(() => {
    setStep(startStep);
  }, [startStep]);

  const [formData, setFormData] = useState(initialData || defaultFormData);

  // State of shape data from drawing manager
  // TODO: move to default form data
  const [shapeData, setShapeData] = useState<ShapeData>({
    type: null,
    data: null,
  });

  useEffect(() => {
    const handleUnload = () => {
      setFormData(defaultFormData);
      setStep(1);
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const goNext = () => {
    if (step === steps.length) {
      formData.selectedCoordinates = serializeShapeData(shapeData);
      onSubmit(formData, isEdit);
    } else {
      setStep((prev) => Math.min(prev + 1, steps.length));
    }
  };

  const goBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  return (
      <Box sx={{ backgroundColor: "#f0f0f0", p: 2, borderRadius: 2, mt: 3 }}>
        <Paper elevation={3} sx={{ p: 3, boxShadow: 0 }}>
          <Box
            sx={{
              backgroundColor: "#F2F2F2",
              border: "1px solid black",
              color: "black",
              px: 2,
              py: 1,
              borderRadius: 1,
              mb: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="h6">{steps[step - 1]}</Typography>
          </Box>

          <CustomStepper step={step} />

          {/* Step 1 */}
          {step === 1 && (
            <Box>
              <GrayBox>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Name *
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formData.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>

              <GrayBox>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Description *
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={formData.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>

              <GrayBox>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Type *
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formData.type}
                  onChange={(e) => updateField("type", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>
            </Box>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Update Interval *
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.updateInterval}
                    onChange={(e) =>
                      updateField("updateInterval", e.target.value)
                    }
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </GrayBox>

                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Load Frequency *
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.loadFrequency}
                    onChange={(e) =>
                      updateField("loadFrequency", e.target.value)
                    }
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </GrayBox>
              </Grid>

              <Grid item xs={12} sm={6}>
                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Update Interval Unit *
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    value={formData.updateIntervalUnit}
                    onChange={(e) =>
                      updateField("updateIntervalUnit", e.target.value)
                    }
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  >
                    {["Seconds", "Minutes", "Hours", "Days"].map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </TextField>
                </GrayBox>

                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Load Frequency Unit *
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    value={formData.loadFrequencyUnit}
                    onChange={(e) =>
                      updateField("loadFrequencyUnit", e.target.value)
                    }
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  >
                    {["Seconds", "Minutes", "Hours", "Days"].map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </TextField>
                </GrayBox>
              </Grid>
            </Grid>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Sector *
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.sector}
                    onChange={(e) => updateField("sector", e.target.value)}
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </GrayBox>

                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Sector Entity *
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.sectorEntity}
                    onChange={(e) =>
                      updateField("sectorEntity", e.target.value)
                    }
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </GrayBox>

                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() =>
                      updateField(
                        "selectedOriginators",
                        formData.selectedOriginators + "Originator\n"
                      )
                    }
                  >
                    + Add Originator
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() =>
                      updateField(
                        "selectedOwners",
                        formData.selectedOwners + "Owner\n"
                      )
                    }
                  >
                    + Add Owner
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      updateField(
                        "selectedOriginators",
                        formData.selectedOriginators + "Originator\n"
                      );
                      updateField(
                        "selectedOwners",
                        formData.selectedOwners + "Owner\n"
                      );
                    }}
                  >
                    + Add Both
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Selected Originator(s)
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={formData.selectedOriginators}
                    onChange={(e) =>
                      updateField("selectedOriginators", e.target.value)
                    }
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </GrayBox>

                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Selected Owner(s)
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={formData.selectedOwners}
                    onChange={(e) =>
                      updateField("selectedOwners", e.target.value)
                    }
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </GrayBox>
              </Grid>
            </Grid>
          )}

          {/* Step 4 */}
          {step === 4 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Source Name *
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.sourceName}
                    onChange={(e) => updateField("sourceName", e.target.value)}
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </GrayBox>

                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Source Type *
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    value={formData.sourceType}
                    onChange={(e) => updateField("sourceType", e.target.value)}
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  >
                    {["Type A", "Type B", "Type C"].map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </GrayBox>

                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Sensor Type *
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    variant="outlined"
                    value={formData.sensorType}
                    onChange={(e) => updateField("sensorType", e.target.value)}
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  >
                    {["Sensor X", "Sensor Y", "Sensor Z"].map((sensor) => (
                      <MenuItem key={sensor} value={sensor}>
                        {sensor}
                      </MenuItem>
                    ))}
                  </TextField>
                </GrayBox>
              </Grid>

              <Grid item xs={12} sm={6}>
                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Manufacturer
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.manufacturer}
                    onChange={(e) =>
                      updateField("manufacturer", e.target.value)
                    }
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </GrayBox>

                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Make
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.make}
                    onChange={(e) => updateField("make", e.target.value)}
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </GrayBox>

                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Model
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.model}
                    onChange={(e) => updateField("model", e.target.value)}
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </GrayBox>

                <GrayBox>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Version
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formData.version}
                    onChange={(e) => updateField("version", e.target.value)}
                    InputProps={{ sx: { backgroundColor: "white" } }}
                  />
                </GrayBox>
              </Grid>
            </Grid>
          )}

          {/* Step 5 */}
          {step === 5 && (
            <Box>
              {/* Radio Groups */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <GrayBox sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Location Type *
                  </Typography>
                  {["Fixed", "Moving", "Other"].map((option) => (
                    <Box
                      key={option}
                      sx={{ display: "flex", alignItems: "center", mb: 1 }}
                    >
                      <input
                        type="radio"
                        id={`locationType-${option}`}
                        name="locationType"
                        value={option}
                        checked={formData.locationType === option}
                        onChange={(e) =>
                          updateField("locationType", e.target.value)
                        }
                      />
                      <label
                        htmlFor={`locationType-${option}`}
                        style={{ marginLeft: "0.5rem" }}
                      >
                        {option}
                      </label>
                    </Box>
                  ))}
                </GrayBox>

                <GrayBox sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Location Sub-type *
                  </Typography>
                  {["Aerial", "Marine", "Spaceborne", "Terrestrial"].map(
                    (option) => (
                      <Box
                        key={option}
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <input
                          type="radio"
                          id={`locationSubtype-${option}`}
                          name="locationSubtype"
                          value={option}
                          checked={formData.locationSubtype === option}
                          onChange={(e) =>
                            updateField("locationSubtype", e.target.value)
                          }
                        />
                        <label
                          htmlFor={`locationSubtype-${option}`}
                          style={{ marginLeft: "0.5rem" }}
                        >
                          {option}
                        </label>
                      </Box>
                    )
                  )}
                </GrayBox>

                <GrayBox sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Category *
                  </Typography>
                  {["Address", "Custom Region"].map((option) => (
                    <Box
                      key={option}
                      sx={{ display: "flex", alignItems: "center", mb: 1 }}
                    >
                      <input
                        type="radio"
                        id={`locationCategory-${option}`}
                        name="locationCategory"
                        value={option}
                        checked={formData.locationCategory === option}
                        onChange={(e) =>
                          updateField("locationCategory", e.target.value)
                        }
                      />
                      <label
                        htmlFor={`locationCategory-${option}`}
                        style={{ marginLeft: "0.5rem" }}
                      >
                        {option}
                      </label>
                    </Box>
                  ))}
                </GrayBox>
              </Box>

              {/* Address Layout */}
              {formData.locationCategory === "Address" && (
                <>
                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Street Address *
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.streetAddress}
                      onChange={(e) =>
                        updateField("streetAddress", e.target.value)
                      }
                      InputProps={{ sx: { backgroundColor: "white" } }}
                    />
                  </GrayBox>

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <GrayBox sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        City *
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={formData.city}
                        onChange={(e) => updateField("city", e.target.value)}
                        InputProps={{ sx: { backgroundColor: "white" } }}
                      />
                    </GrayBox>

                    <GrayBox sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Postal Code *
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={formData.postalCode}
                        onChange={(e) =>
                          updateField("postalCode", e.target.value)
                        }
                        InputProps={{ sx: { backgroundColor: "white" } }}
                      />
                    </GrayBox>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2 }}>
                    <GrayBox sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        State / Province *
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={formData.stateProvince}
                        onChange={(e) =>
                          updateField("stateProvince", e.target.value)
                        }
                        InputProps={{ sx: { backgroundColor: "white" } }}
                      />
                    </GrayBox>

                    <GrayBox sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Country *
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={formData.country}
                        onChange={(e) => updateField("country", e.target.value)}
                        InputProps={{ sx: { backgroundColor: "white" } }}
                      />
                    </GrayBox>
                  </Box>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Notes
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      variant="outlined"
                      value={formData.geoNotes}
                      onChange={(e) => updateField("geoNotes", e.target.value)}
                      InputProps={{ sx: { backgroundColor: "white" } }}
                    />
                  </GrayBox>
                </>
              )}

              {formData.locationCategory === "Custom Region" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box
                      sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                    >
                      <GrayBox sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Notes
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          variant="outlined"
                          value={formData.geoNotes}
                          onChange={(e) =>
                            updateField("geoNotes", e.target.value)
                          }
                          InputProps={{ sx: { backgroundColor: "white" } }}
                        />
                      </GrayBox>

                      <GrayBox
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            Custom Region Data
                          </Typography>
                        </Box>
                        <TextField
                          disabled
                          fullWidth
                          multiline
                          variant="outlined"
                          value={
                            shapeData.type === "polygon"
                              ? shapeData.data.path.reduce(
                                  (
                                    joined: string,
                                    latLng: google.maps.LatLng,
                                    i: number
                                  ) => {
                                    return `${joined}\n${i}: Lat: ${latLng.lat()}, Lng: ${latLng.lng()}`;
                                  },
                                  "Path:"
                                )
                              : shapeData.type === "rectangle"
                              ? `Bounds:\n` +
                                `North: ${shapeData.data.bounds.north}\n` +
                                `East: ${shapeData.data.bounds.east}\n` +
                                `South: ${shapeData.data.bounds.south}\n` +
                                `West: ${shapeData.data.bounds.west}`
                              : shapeData.type === "circle"
                              ? `Center:\n` +
                                `Lat: ${shapeData.data.center.lat}, Lng: ${shapeData.data.center.lng}\n` +
                                `Radius (in meters): ${shapeData.data.radius}`
                              : null
                          }
                          sx={{ flex: 1 }}
                          InputProps={{
                            sx: {
                              backgroundColor: "white",
                              alignItems: "flex-start",
                              height: "100%",
                              "&.Mui-disabled": {
                                color: "black !important",
                              },
                            },
                          }}
                        />
                      </GrayBox>

                      <Box>
                        <Label>Map Preview</Label>
                        <MapCanvas>
                          <APIProvider
                            apiKey={"AIzaSyBy0cowEjTB0suohBM4h4UdiSz-ng-YPWg"}
                          >
                            <Map
                              category={formData.locationCategory}
                              handleChangeShapeData={setShapeData}
                            />
                          </APIProvider>
                        </MapCanvas>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              {(formData.locationCategory === "Bounded Box" ||
                formData.locationCategory === "Polygon" ||
                formData.locationCategory === "Exact Coordinates" ||
                formData.locationCategory === "Custom Region") && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                ></Box>
              )}
            </Box>
          )}

          {/* Step 6 */}
          {step === 6 && (
            <Box>
              <GrayBox>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Environment *
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formData.environment}
                  onChange={(e) => updateField("environment", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>

              <GrayBox>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Interactor
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formData.interactor}
                  onChange={(e) => updateField("interactor", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>

              <GrayBox>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Container Name
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formData.containerName}
                  onChange={(e) => updateField("containerName", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>
            </Box>
          )}

          {/* Step 7 */}
          {step === 7 && (
            <Box>
              <GrayBox>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Legal Type *
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formData.legalType}
                  onChange={(e) => updateField("legalType", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>

              <GrayBox>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Regulatory Type
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formData.regulatoryType}
                  onChange={(e) =>
                    updateField("regulatoryType", e.target.value)
                  }
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>

              <GrayBox>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Citation
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formData.citation}
                  onChange={(e) => updateField("citation", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>
            </Box>
          )}

          {/* Step 8 */}
          {step === 8 && (
            <Box>
              <GrayBox>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  File Type *
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formData.fileType}
                  onChange={(e) => updateField("fileType", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>

              <GrayBox>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Unit of Measure
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formData.unitOfMeasure}
                  onChange={(e) => updateField("unitOfMeasure", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>

              <GrayBox>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Schema Body
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  variant="outlined"
                  value={formData.schemaBody}
                  onChange={(e) => updateField("schemaBody", e.target.value)}
                  InputProps={{ sx: { backgroundColor: "white" } }}
                />
              </GrayBox>
            </Box>
          )}

          {step === 9 && (
            <Box>
              <Grid container spacing={2}>
                {/* Left Column */}
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" gutterBottom>Describe</Typography>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Name</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.name}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Description</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      variant="outlined"
                      value={formData.description}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Type</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.type}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Continuous Feed Info</Typography>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Update Interval</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.updateInterval}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Update Interval Unit</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.updateIntervalUnit}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Load Frequency</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.loadFrequency}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Load Frequency Unit</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.loadFrequencyUnit}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Ownership</Typography>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Originators</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      value={formData.selectedOriginators}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Owners</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      value={formData.selectedOwners}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>
                </Grid>

                {/* Center Column */}
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" gutterBottom>Source & Sensor</Typography>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Source Name</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.sourceName}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Source Type</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.sourceType}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Sensor Type</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.sensorType}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Manufacturer</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.manufacturer}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Make</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.make}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Model</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.model}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Version</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.version}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Geolocation</Typography>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Location Type</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.locationType}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Location Subtype</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.locationSubtype}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Location Category</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.locationCategory}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  {formData.locationCategory === 'Address' && (
                    <>
                      <GrayBox>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Street Address</Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={formData.streetAddress}
                          disabled
                          InputProps={{ sx: { backgroundColor: 'white' } }}
                        />
                      </GrayBox>
                      <GrayBox>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>City</Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={formData.city}
                          disabled
                          InputProps={{ sx: { backgroundColor: 'white' } }}
                        />
                      </GrayBox>
                      <GrayBox>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Postal Code</Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={formData.postalCode}
                          disabled
                          InputProps={{ sx: { backgroundColor: 'white' } }}
                        />
                      </GrayBox>
                      <GrayBox>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>State / Province</Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={formData.stateProvince}
                          disabled
                          InputProps={{ sx: { backgroundColor: 'white' } }}
                        />
                      </GrayBox>
                      <GrayBox>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Country</Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={formData.country}
                          disabled
                          InputProps={{ sx: { backgroundColor: 'white' } }}
                        />
                      </GrayBox>
                      <GrayBox>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Notes</Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          variant="outlined"
                          value={formData.geoNotes}
                          disabled
                          InputProps={{ sx: { backgroundColor: 'white' } }}
                        />
                      </GrayBox>
                    </>
                  )}

                  {/* You can add more conditional geolocation fields here as needed */}
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" gutterBottom>Environment</Typography>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Environment</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.environment}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Interactor</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.interactor}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Container Name</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.containerName}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Legal & Licensing</Typography>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Legal Type</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.legalType}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Regulatory Type</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.regulatoryType}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Citation</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.citation}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Schema</Typography>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>File Type</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.fileType}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Unit of Measure</Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.unitOfMeasure}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>

                  <GrayBox>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Schema Body</Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      variant="outlined"
                      value={formData.schemaBody}
                      disabled
                      InputProps={{ sx: { backgroundColor: 'white' } }}
                    />
                  </GrayBox>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Navigation */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              onClick={goBack}
              disabled={step === 1}
              variant="text"
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: "1rem",
                minWidth: 0,
                padding: 0,
                color: "black",
                '&:hover': { background: "transparent", textDecoration: "underline", color: "black" },
                mr: 2,
                display: "flex",
                alignItems: "center"
              }}
              startIcon={<ArrowBackIcon sx={{ fontSize: "1.1em", color: "black" }} />}
            >
              Previous
            </Button>
            <Button
              onClick={goNext}
              variant="text"
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: "1rem",
                minWidth: 0,
                padding: 0,
                color: "black",
                '&:hover': { background: "transparent", textDecoration: "underline", color: "black" },
                display: "flex",
                alignItems: "center"
              }}
              endIcon={step === steps.length ? null : <ArrowForwardIcon sx={{ fontSize: "1.1em", color: "black" }} />}
            >
              {step === steps.length ? "Finish" : "Next"}
            </Button>
          </Box>
        </Paper>
      </Box>
  );
}

export default CreateMetadata;